import type { Source } from './registry';

export type SourcesBadgeState = 'none' | 'info' | 'amber' | 'red';

/**
 * Derives the single Sources-nav signal from the source list
 * (ADR-0029 §4 + the 2026-06-15 amendment §B). Precedence, highest first:
 *
 *   red   — ≥1 source `reauth_required` (was connected, now broken). Durable.
 *   amber — ≥1 source `upstream_error` (transient).
 *   info  — zero sources connected (never connected, or user-disconnected).
 *   none  — at least one source connected and nothing broken.
 *
 * `info` is a calm nudge, never the red "broken" register, so "removed ≠ broken"
 * holds at the colour level. An empty list (sources not yet loaded) yields
 * `none` so the nudge never flashes before the first fetch resolves.
 */
export function sourcesBadgeState(sources: readonly Source[]): SourcesBadgeState {
  if (sources.length === 0) return 'none';
  if (sources.some(s => s.status === 'reauth_required')) return 'red';
  if (sources.some(s => s.status === 'upstream_error')) return 'amber';
  if (sources.every(s => s.status !== 'connected')) return 'info';
  return 'none';
}
